// TenantCreditCard — Dashboard widget showing the tenant's Credit Builder
// status. If enrolled: shows on-time rate, months reported, score-lift
// progress, and a "View details" link. If not enrolled: shows a friendly
// CTA encouraging them to start.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

export default function TenantCreditCard({ email }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) {
      setLoading(false);
      return;
    }
    fetch(`/api/credit-builder/status?email=${encodeURIComponent(email)}`)
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus({ enrolled: false }))
      .finally(() => setLoading(false));
  }, [email]);

  if (loading) {
    return (
      <div className="mt-4 p-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 animate-pulse" data-testid="tenant-credit-card-loading">
        <div className="h-4 w-32 bg-emerald-100 rounded"/>
        <div className="h-8 w-24 bg-emerald-100 rounded mt-3"/>
      </div>
    );
  }

  // Not enrolled state — friendly conversion CTA
  if (!status?.enrolled) {
    return (
      <div className="mt-4 p-5 rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200" data-testid="tenant-credit-card-cta">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none">
            <TrendingUp className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                Free for tenants
              </div>
              <Sparkles className="w-3 h-3 text-emerald-600"/>
            </div>
            <div className="font-display text-base font-bold text-slate-900 mt-1">
              Turn your rent into credit history
            </div>
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              Mark Credit Builder reports your on-time payments to Equifax + TransUnion Canada. Most tenants see +25-50 points in 12 months.
            </p>
            <Link to="/credit-builder" data-testid="tenant-credit-cta-enroll"
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700">
              Start building credit <ArrowRight className="w-3 h-3"/>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Enrolled state — celebrate the progress
  const rate = status.on_time_rate ?? 0;
  const tone = rate >= 90 ? "emerald" : rate >= 70 ? "amber" : "rose";
  const tones = {
    emerald: { ring: "border-emerald-300", text: "text-emerald-700", pill: "bg-emerald-100 text-emerald-700" },
    amber:   { ring: "border-amber-300",   text: "text-amber-700",   pill: "bg-amber-100 text-amber-700" },
    rose:    { ring: "border-rose-300",    text: "text-rose-700",    pill: "bg-rose-100 text-rose-700" },
  };
  const t = tones[tone];

  return (
    <div className={`mt-4 p-5 rounded-2xl bg-white border-2 ${t.ring}`} data-testid="tenant-credit-card-enrolled">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none">
            <ShieldCheck className="w-5 h-5"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3"/> Mark Credit Builder
            </div>
            <div className="font-display text-base font-bold text-slate-900 mt-1">
              {status.tenant_name || "Welcome"} — your credit is growing
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {status.months_reported} {status.months_reported === 1 ? "month" : "months"} reported · {status.province_code}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">On-time rate</div>
          <div className={`font-display text-3xl font-bold tabular-nums ${t.text}`} data-testid="tenant-credit-ontimerate">{rate}%</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <Pill testid="tenant-credit-pill-bureaus" label="Bureaus" value="Equifax + TU"/>
        <Pill testid="tenant-credit-pill-status" label="Last month"
          value={status.last_status ? status.last_status.replace("paid_", "").replace("_", " ") : "—"}
          accent={status.last_status === "paid_on_time" ? "emerald" : "slate"}/>
        <Pill testid="tenant-credit-pill-period" label="Last period" value={status.last_period || "—"}/>
      </div>
      <Link to="/credit-builder" data-testid="tenant-credit-card-link"
        className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-emerald-700 hover:text-emerald-900">
        View your credit progress <ArrowRight className="w-3 h-3"/>
      </Link>
    </div>
  );
}

function Pill({ label, value, accent, testid }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`p-2 rounded-lg border text-center ${tones[accent] || tones.slate}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-xs font-bold truncate">{value}</div>
    </div>
  );
}
